import React, { useState, useEffect, useCallback } from 'react';
import { Card, DatePicker, Button, Table, Statistic, Row, Col, Tag, Select, message } from 'antd';
import dayjs from 'dayjs';
import { getRoutinesReport } from '../../api/reports';
import { apiErrorMessage } from '../../utils/apiError';

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Tamamlandı' },
  { value: 'overdue', label: 'Gecikti' },
  { value: 'pending', label: 'Bekliyor' },
];

const STATUS_META = {
  completed: ['green', 'Tamamlandı'],
  overdue: ['red', 'Gecikti'],
  pending: ['orange', 'Bekliyor'],
};

const RoutinesReportTab = () => {
  const [range, setRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  // Siralama sunucuda: liste sayfali oldugu icin istemci tarafi siralama
  // yalnizca gorunen sayfayi sirasi ve yaniltirdi.
  const [sorter, setSorter] = useState({ field: null, order: null });

  const fetch = useCallback(async (targetPage = 1, sort = sorter) => {
    if (!range) return;
    setLoading(true);
    try {
      const res = await getRoutinesReport({
        date_from: range[0].format('YYYY-MM-DD'),
        date_to: range[1].format('YYYY-MM-DD'),
        status: status || undefined,
        page: targetPage,
        per_page: 20,
        sort_by: sort?.field || undefined,
        sort_direction: sort?.order === 'ascend' ? 'asc' : (sort?.order === 'descend' ? 'desc' : undefined),
      });
      setData(res.data);
      setPage(targetPage);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Rutin raporu yüklenemedi.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, status]);

  useEffect(() => { fetch(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // sorter: true = siralamayi sunucu yapiyor (bkz. fetch). Kolon adlari
  // backend'deki beyaz listeyle birebir ayni olmali.
  const sortProps = (field) => ({
    sorter: true,
    sortOrder: sorter.field === field ? sorter.order : null,
  });

  const columns = [
    { title: 'Rutin', dataIndex: 'name', ...sortProps('name') },
    { title: 'Şube', dataIndex: 'branch_name', ...sortProps('branch_name') },
    { title: 'Tarih', dataIndex: 'date', ...sortProps('date'), render: (v) => dayjs(v).format('DD.MM.YYYY') },
    { title: 'Saat', dataIndex: 'scheduled_time', ...sortProps('scheduled_time') },
    {
      title: 'Durum',
      dataIndex: 'status',
      ...sortProps('status'),
      render: (v) => {
        const [color, label] = STATUS_META[v] || ['default', v];
        return <Tag color={color}>{label}</Tag>;
      },
    },
    { title: 'Tamamlayan', dataIndex: 'completed_by', ...sortProps('completed_by'), render: (v) => v || '—' },
    { title: 'Gecikme (dk)', dataIndex: 'minutes_late', ...sortProps('minutes_late'), render: (v) => v ?? '—' },
  ];

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <RangePicker value={range} onChange={setRange} format="DD.MM.YYYY" />
        <Select
          allowClear
          placeholder="Duruma göre filtrele"
          style={{ width: 200 }}
          value={status}
          onChange={(v) => setStatus(v || null)}
          options={STATUS_OPTIONS}
        />
        <Button type="primary" onClick={() => fetch(1)} loading={loading}>Getir</Button>
      </div>

      {data && (
        <>
          <Row gutter={16} className="mb-4">
            <Col span={6}>
              <Card size="small">
                <Statistic title="Toplam Rutin" value={data.total} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="Tamamlanma Oranı" value={data.completion_rate} suffix="%" valueStyle={{ color: '#52c41a' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="Gecikti" value={data.overdue} valueStyle={{ color: '#ff4d4f' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="Ortalama Gecikme" value={data.avg_delay} suffix="dk" />
              </Card>
            </Col>
          </Row>

          {data.per_user?.length > 0 && (
            <Card size="small" title="Kullanıcı Bazlı Tamamlama" className="mb-4">
              {data.per_user.map((u) => (
                <Tag key={u.user_id}>{u.user_name}: {u.count}</Tag>
              ))}
            </Card>
          )}

          <Table
            dataSource={data.executions}
            rowKey="id"
            columns={columns}
            size="small"
            // Sayfalama ve siralama tek elden: pagination.onChange ayrica
            // taniml kalsaydi sayfa degisiminde iki istek giderdi.
            onChange={(pag, _filters, nextSorter) => {
              const next = nextSorter?.order
                ? { field: nextSorter.field, order: nextSorter.order }
                : { field: null, order: null };
              const sortDegisti = next.field !== sorter.field || next.order !== sorter.order;
              setSorter(next);
              fetch(sortDegisti ? 1 : pag.current, next);
            }}
            pagination={{
              current: page,
              pageSize: 20,
              total: data.total,
            }}
          />
        </>
      )}
    </div>
  );
};

export default RoutinesReportTab;
